//------------------------------------------------------------------------------
// 住所選択入力コンポーネント
//------------------------------------------------------------------------------
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { searchMap } from '@/common/common';
import { renderSelect } from '@/components/atoms/CustomPart';
import pulldown from '@/utils/pulldown-utils';
import address from '@/services/address-search';

/*
 * 都道府県選択コンポーネント (フォーム入力項目)
 *
 * @param {object} props プロパティ
 */
export const TodofukenField = React.memo(({
  name, label,
  genericMap,
  withEmpty, onChange, ...otherProps
}) => {

  // 都道府県リスト取得
  const dataList = searchMap(genericMap, 'prefectures');

  // プルダウン項目作成
  const emptyList = withEmpty ? pulldown.EMPTY_LIST : [];
  const selectItems = [
    ...emptyList,
    ...dataList.map((gen) => pulldown.makeItem(gen.cd, gen.cdName)),
  ];

  // デバッグログ
  if (!console.log?.disabled) {
    console.debug('Render 都道府県',
        `data=${dataList.length}`,
        `item=${selectItems.length}`);
  }

  // 描画
  return (
    <Field
      name={name}
      label={label || '都道府県'}
      fullWidth
      component={renderSelect}
      onChange={onChange}
      {...otherProps}
    >
      {selectItems}
    </Field>
  );
});

// 都道府県プロパティ
TodofukenField.propTypes = {
  name: PropTypes.string.isRequired,   // フォーム項目名
  label: PropTypes.string,             // 項目表示ラベル
  withEmpty: PropTypes.bool,           // 空白項目の有無
  onChange: PropTypes.func,            // 変更ハンドラー
  genericMap: PropTypes.object,        // 汎用マスタマップ
  otherProps: PropTypes.object,        // その他プロパティ
};

/**
 * 市区町村選択コンポーネント (フォーム入力項目)
 *
 * @param {object} props プロパティ
 */
export const SikutyosonField = React.memo(({
  name, label,
  inputValue, upperValue,
  withEmpty, onChange, ...otherProps
}) => {

  // 市区町村リスト
  const [ dataList, setDataList ] = useState([]);

  // 市区町村リスト取得
  useEffect(() => {
    const loadList = async () => {
      const newList = await address.municipalities(upperValue);
      setDataList(newList);
    };
    loadList();
  }, [upperValue]);

  // リスト取得前の初期値
  const initData = inputValue ? { value: inputValue } : null;

  // プルダウン項目作成
  const emptyList = withEmpty ? pulldown.EMPTY_LIST : [];
  const selectItems = [
    ...emptyList,
    ...pulldown.makeItems(dataList, initData),
  ];

  // デバッグログ
  if (!console.log?.disabled) {
    console.debug('Render 市区町村',
        `value=${inputValue}`,
        `upper=${upperValue}`,
        `items=${selectItems.length}`);
  }

  // 描画
  return (
    <Field
      name={name}
      label={label || '市区町村'}
      fullWidth
      component={renderSelect}
      onChange={onChange}
      {...otherProps}
    >
      {selectItems}
    </Field>
  );
});

// 市区町村プロパティ
SikutyosonField.propTypes = {
  name: PropTypes.string.isRequired,    // フォーム項目名
  label: PropTypes.string,              // 項目表示ラベル
  inputValue: PropTypes.string,         // 市区町村コード入力値(12桁)
  upperValue: PropTypes.string,         // 上位の都道府県コード(2桁)
  withEmpty: PropTypes.bool,            // 空白項目の有無
  onChange: PropTypes.func,             // 変更ハンドラー
  otherProps: PropTypes.object,         // その他プロパティ
};

/**
 * 大字通称選択コンポーネント (フォーム入力項目)
 *
 * @param {object} props プロパティ
 */
export const OazatusyoField = React.memo(({
  name, label,
  inputValue, upperValue,
  withEmpty, onChange, ...otherProps
}) => {

  // 大字通称リスト
  const [ dataList, setDataList ] = useState([]);

  // 大字通称リスト取得
  useEffect(() => {
    const loadList = async () => {
      const newList = await address.oazatusyo(upperValue);
      setDataList(newList);
    };
    loadList();
  }, [upperValue]);

  // リスト取得前の初期値
  const initData = inputValue ? { value: inputValue } : null;

  // プルダウン項目作成
  const emptyList = withEmpty ? pulldown.EMPTY_LIST : [];
  const selectItems = [
    ...emptyList,
    ...pulldown.makeItems(dataList, initData),
  ];

  // デバッグログ
  if (!console.log?.disabled) {
    console.debug('Render 大字通称',
        `value=${inputValue}`,
        `upper=${upperValue}`,
        `items=${selectItems.length}`);
  }

  // 描画
  return (
    <Field
      name={name}
      label={label || '大字通称'}
      fullWidth
      component={renderSelect}
      onChange={onChange}
      {...otherProps}
    >
      {selectItems}
    </Field>
  );
});

// 大字通称プロパティ
OazatusyoField.propTypes = {
  name: PropTypes.string.isRequired,    // フォーム項目名
  label: PropTypes.string,              // 項目表示ラベル
  inputValue: PropTypes.string,         // 大字通称コード入力値(12桁)
  upperValue: PropTypes.string,         // 上位の市区町村コード(12桁)
  withEmpty: PropTypes.bool,            // 空白項目の有無
  onChange: PropTypes.func,             // 変更ハンドラー
  otherProps: PropTypes.object,         // その他プロパティ
};

/**
 * 字丁目選択コンポーネント (フォーム入力項目)
 *
 * @param {object} props プロパティ
 */
export const AzatyomeField = React.memo(({
  name, label,
  inputValue, upperValue,
  withEmpty, ...otherProps
}) => {

  // 字丁目リスト
  const [ dataList, setDataList ] = useState([]);

  // 字丁目リスト取得
  useEffect(() => {
    const loadList = async () => {
      const newList = await address.azatyome(upperValue);
      setDataList(newList);
    };
    loadList();
  }, [upperValue]);

  // リスト取得前の初期値
  const initData = inputValue ? { value: inputValue } : null;

  // プルダウン項目作成
  const emptyList = withEmpty ? pulldown.EMPTY_LIST : [];
  const selectItems = [
    ...emptyList,
    ...pulldown.makeItems(dataList, initData),
  ];

  // デバッグログ
  if (!console.log?.disabled) {
    console.debug('Render 字丁目',
        `value=${inputValue}`,
        `upper=${upperValue}`,
        `items=${selectItems.length}`);
  }

  // 描画
  return (
    <Field
      name={name}
      label={label || '字丁目'}
      fullWidth
      component={renderSelect}
      {...otherProps}
    >
      {selectItems}
    </Field>
  );
});

// 字丁目プロパティ
AzatyomeField.propTypes = {
  name: PropTypes.string.isRequired,    // フォーム項目名
  label: PropTypes.string,              // 項目表示ラベル
  inputValue: PropTypes.string,         // 字丁目コード入力値(12桁)
  upperValue: PropTypes.string,         // 上位の大字通称コード(12桁)
  withEmpty: PropTypes.bool,            // 空白項目の有無
  otherProps: PropTypes.object,         // その他プロパティ
};

// デフォルトエクスポート
export default {
  TodofukenField,
  SikutyosonField,
  OazatusyoField,
  AzatyomeField,
};
